/**
 * division-context.js
 * Kaya Theme — Division Context System
 *
 * Manages the division context (main/hombre/mujer/accesorios) so the header
 * menu, utility-header active tab, and all internal links stay consistent
 * across collection, product, search, and other templates.
 *
 * Flow:
 *  1. Read server-detected division from window.__kayaDivision.fromServer
 *  2. If present → persist to localStorage + cookie (30 days)
 *  3. If absent  → read stored value as fallback
 *  4. Propagate ?division= to every internal <a> on the page
 *  5. Toggle [data-division-menu] containers in the header
 *  6. Highlight active utility-header tab
 *  7. MutationObserver re-processes dynamically added links
 *  8. Wrap history.pushState / replaceState to preserve param in AJAX nav
 */
(function () {
  'use strict';

  /* ── Constants ─────────────────────────────────────────────── */

  var STORAGE_KEY       = 'kaya_division';
  var COOKIE_NAME       = 'kaya_division';
  var COOKIE_DAYS       = 30;
  var ALLOWED           = ['main', 'hombre', 'mujer', 'accesorios', 'bodega'];
  var EXCLUDED_PATHS    = ['/checkout', '/cart', '/account'];
  var ASSET_RE          = /\.(js|css|png|jpe?g|gif|svg|webp|ico|woff2?|ttf|eot|mp4|webm|pdf)(\?|$)/i;

  /* ── Cookie / Storage helpers ──────────────────────────────── */

  function getCookie(name) {
    var m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return m ? decodeURIComponent(m[2]) : null;
  }

  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 86400000);
    document.cookie =
      name + '=' + encodeURIComponent(value) +
      ';expires=' + d.toUTCString() +
      ';path=/;SameSite=Lax';
  }

  function getStored() {
    try {
      return localStorage.getItem(STORAGE_KEY) || getCookie(COOKIE_NAME) || '';
    } catch (_) {
      return getCookie(COOKIE_NAME) || '';
    }
  }

  function setStored(value) {
    try { localStorage.setItem(STORAGE_KEY, value); } catch (_) { /* noop */ }
    setCookie(COOKIE_NAME, value, COOKIE_DAYS);
  }

  function isAllowed(v) {
    return ALLOWED.indexOf(v) !== -1;
  }

  /* ── URL helpers ───────────────────────────────────────────── */

  function isInternalLink(href) {
    if (!href) return false;
    if (/^(mailto:|tel:|#|javascript:)/i.test(href)) return false;
    try {
      var url = new URL(href, location.origin);
      if (url.origin !== location.origin) return false;
      for (var i = 0; i < EXCLUDED_PATHS.length; i++) {
        if (url.pathname.indexOf(EXCLUDED_PATHS[i]) === 0) return false;
      }
      if (ASSET_RE.test(url.pathname)) return false;
      return true;
    } catch (_) {
      return false;
    }
  }

  function addDivision(href, division) {
    try {
      var url = new URL(href, location.origin);
      url.searchParams.set('division', division);
      return url.pathname + url.search + url.hash;
    } catch (_) {
      // Fallback: plain string manipulation
      if (/[?&]division=/.test(href)) {
        return href.replace(/([?&])division=[^&#]*/, '$1division=' + division);
      }
      return href + (href.indexOf('?') > -1 ? '&' : '?') + 'division=' + division;
    }
  }

  /* ── Determine active division ─────────────────────────────── */

  var serverVal = (window.__kayaDivision && window.__kayaDivision.fromServer) || '';
  var storedVal = getStored();
  var active;

  if (serverVal && isAllowed(serverVal)) {
    active = serverVal;
    setStored(active);
  } else if (storedVal && isAllowed(storedVal)) {
    active = storedVal;
  } else {
    active = 'main';
  }

  // Expose globally
  window.__kayaDivision         = window.__kayaDivision || {};
  window.__kayaDivision.active  = active;

  // CSS hook on <html>
  document.documentElement.setAttribute('data-division', active);

  /* ── Process individual link ───────────────────────────────── */

  function processLink(el) {
    if (el.hasAttribute('data-division-processed')) return;
    if (el.hasAttribute('data-division'))           return; // utility-header tabs
    if (el.closest('.header__logo, .utility-header__logo')) return; // logo = reset to main
    var href = el.getAttribute('href');
    if (!href || !isInternalLink(href)) return;

    // Only propagate non-main divisions (main = default, keeps URLs clean)
    if (active && active !== 'main') {
      el.setAttribute('href', addDivision(href, active));
    }
    el.setAttribute('data-division-processed', '');
  }

  function processAllLinks() {
    var links = document.querySelectorAll(
      'a[href]:not([data-division-processed]):not([data-division])'
    );
    for (var i = 0; i < links.length; i++) processLink(links[i]);
  }

  /* ── Toggle header desktop menus ───────────────────────────── */

  function updateHeaderMenus() {
    var containers = document.querySelectorAll('[data-division-menu]');
    if (!containers.length) return;

    var found = false;
    for (var i = 0; i < containers.length; i++) {
      if (containers[i].getAttribute('data-division-menu') === active) {
        containers[i].classList.add('division-active');
        found = true;
      } else {
        containers[i].classList.remove('division-active');
      }
    }

    // Fallback: if no menu matches active division, show main
    if (!found) {
      for (var j = 0; j < containers.length; j++) {
        if (containers[j].getAttribute('data-division-menu') === 'main') {
          containers[j].classList.add('division-active');
        }
      }
    }
  }

  /* ── Toggle mobile sidebar (hamburger targets correct sidebar) ── */

  function updateSidebarMenu() {
    var hamburgers = document.querySelectorAll('.header__hamburger-btn');
    if (!hamburgers.length) return;

    var targetId = 'sidebar-menu-' + active;

    // Verify the target sidebar exists; fall back to main if not
    if (!document.getElementById(targetId)) {
      targetId = 'sidebar-menu-main';
    }

    for (var i = 0; i < hamburgers.length; i++) {
      hamburgers[i].setAttribute('aria-controls', targetId);
    }
  }

  /* ── Utility header: active state & click handling ─────────── */

  function setupLogoReset() {
    var logos = document.querySelectorAll('.header__logo a, .utility-header__logo-link');
    for (var i = 0; i < logos.length; i++) {
      logos[i].addEventListener('click', function () {
        setStored('main');
      });
    }
  }

  function setupUtilityHeader() {
    var tabs = document.querySelectorAll('.utility-header__menu-link[data-division]');

    for (var i = 0; i < tabs.length; i++) {
      (function (tab) {
        var tabDiv = tab.getAttribute('data-division');

        // Always add ?division= to every tab href (including main, for explicit reset)
        var href = tab.getAttribute('href');
        if (href && isInternalLink(href)) {
          tab.setAttribute('href', addDivision(href, tabDiv));
        }

        // Mark the active tab
        if (tabDiv === active) {
          tab.classList.add('utility-header__menu-link--active');
        }

        // On click: persist this tab's division
        tab.addEventListener('click', function () {
          setStored(tabDiv);
        });
      })(tabs[i]);
    }
  }

  /* ── Intercept history state changes (filters, sort, AJAX) ── */

  function wrapHistory(method) {
    var original = history[method];
    history[method] = function (state, title, url) {
      if (url && active && active !== 'main') {
        var s = url.toString();
        if (isInternalLink(s) && !/[?&]division=/.test(s)) {
          url = addDivision(s, active);
        }
      }
      return original.call(this, state, title, url);
    };
  }

  wrapHistory('pushState');
  wrapHistory('replaceState');

  /* ── MutationObserver for dynamic content ──────────────────── */

  function setupObserver() {
    if (typeof MutationObserver === 'undefined') return;

    new MutationObserver(function (mutations) {
      for (var m = 0; m < mutations.length; m++) {
        var nodes = mutations[m].addedNodes;
        for (var n = 0; n < nodes.length; n++) {
          var node = nodes[n];
          if (node.nodeType !== 1) continue;
          if (node.tagName === 'A') processLink(node);
          var inner = node.querySelectorAll
            ? node.querySelectorAll('a[href]:not([data-division-processed])')
            : [];
          for (var l = 0; l < inner.length; l++) processLink(inner[l]);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ── Bootstrap ─────────────────────────────────────────────── */

  function init() {
    updateHeaderMenus();
    updateSidebarMenu();
    setupLogoReset();
    setupUtilityHeader();
    processAllLinks();
    setupObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
